import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, RefreshCw, CheckCircle2, MapPin, Calendar,
  PoundSterling, ChevronRight, Star
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const ContractorCompletedJobs = () => {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);

  const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contractors/jobs?status=completed`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      } else {
        toast.error('Failed to load jobs');
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="contractor-completed-jobs">
      {/* Header */}
      <div className="mb-6">
        <Link to="/contractor/dashboard" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Dashboard
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Completed Jobs</h1>
            <p className="text-slate-600">Your job history</p>
          </div>
          <Button variant="outline" onClick={fetchJobs} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary */}
      {jobs.length > 0 && (
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-6">
              <div>
                <p className="text-sm text-slate-500">Total Jobs</p>
                <p className="text-2xl font-bold">{jobs.length}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Earned</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(jobs.reduce((sum, j) => sum + (parseFloat(j.final_amount) || 0), 0))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Jobs List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : jobs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No completed jobs yet</h3>
            <p className="text-slate-500 mt-1">
              Completed jobs will appear here
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <Link 
              key={job.id} 
              to={`/contractor/jobs/${job.id}`}
              className="block"
            >
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">
                            {job.title || job.description?.slice(0, 50)}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className="bg-green-100 text-green-700">
                              Completed
                            </Badge>
                            {job.category && (
                              <Badge variant="outline" className="text-xs">
                                {job.category}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-4 mt-4 text-sm text-slate-500">
                        <div className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {job.property_address || 'Address N/A'}
                        </div>
                        {job.completed_at && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(job.completed_at).toLocaleDateString('en-GB')}
                          </div>
                        )}
                        {job.final_amount && (
                          <div className="flex items-center gap-1 text-green-600 font-medium">
                            <PoundSterling className="h-4 w-4" />
                            {formatCurrency(job.final_amount)}
                          </div>
                        )}
                      </div>
                    </div>

                    <ChevronRight className="h-5 w-5 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractorCompletedJobs;
